import React, { useMemo, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Sparkles, Crown, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant, DjayAccessory } from '../../mascot';
import { AppState } from '../../app/types';
import { MODULES } from '../../content';
interface AchievementsSectionProps {
  state: AppState;
  totalQuestionsAnswered: number;
  masteredCount: number;
  currentStreak: number;
}
type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'legendary';
interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  category: 'onboarding' | 'streak' | 'mastery' | 'level' | 'special';
  rarity: AchievementRarity;
  djayEmotionUnlocked: DjayEmotion;
  djayEmotionLocked: DjayEmotion;
  djayVariantUnlocked?: DjayVariant;
  accessory: DjayAccessory;
  check: (data: AchievementCheckData) => boolean;
  getProgress?: (data: AchievementCheckData) => {
    current: number;
    max: number;
    label: string;
  };
}
interface AchievementCheckData {
  totalQuestionsAnswered: number;
  masteredCount: number;
  level: number;
  xp: number;
  // Dagelijkse streak (§15)
  currentWeekDays: number; // 0-5, schooldagen deze week
  consecutiveFullWeeks: number; // Opeenvolgende volle weken
  // Antwoord-streak (§14)
  longestTotalStreak: number; // Persoonlijk record correct-op-rij
  // Dagelijks doel
  totalDailyGoalsReached: number; // Lifetime count
  // Perfecte sessies
  totalPerfectSessions: number; // Lifetime count
}
const ACHIEVEMENTS: Achievement[] = [
// Eerste Stappen (Onboarding)
{
  id: 'first-steps',
  name: 'Eerste Stappen',
  description: 'Beantwoord 5 vragen',
  emoji: '🌱',
  category: 'onboarding',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'seedling',
  check: data => data.totalQuestionsAnswered >= 5,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 5),
    max: 5,
    label: 'vragen'
  })
}, {
  id: 'getting-started',
  name: 'Lekker Bezig',
  description: 'Beantwoord 50 vragen',
  emoji: '📝',
  category: 'onboarding',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'pencil',
  check: data => data.totalQuestionsAnswered >= 50,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 50),
    max: 50,
    label: 'vragen'
  })
}, {
  id: 'century',
  name: 'Honderdtal',
  description: 'Beantwoord 100 vragen',
  emoji: '💯',
  category: 'onboarding',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'pixel',
  accessory: 'badge100',
  check: data => data.totalQuestionsAnswered >= 100,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 100),
    max: 100,
    label: 'vragen'
  })
},
// Reeksen (Streaks)
{
  id: 'three-days',
  name: 'Drie Dagen',
  description: 'Oefen 3 schooldagen deze week',
  emoji: '🔥',
  category: 'streak',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'smallFlame',
  check: data => data.currentWeekDays >= 3,
  getProgress: data => ({
    current: Math.min(data.currentWeekDays, 3),
    max: 3,
    label: 'dagen'
  })
}, {
  id: 'week-champion',
  name: 'Week Kampioen',
  description: 'Oefen een hele schoolweek (5/5)',
  emoji: '🏆',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.currentWeekDays >= 5,
  getProgress: data => ({
    current: Math.min(data.currentWeekDays, 5),
    max: 5,
    label: 'dagen'
  })
}, {
  id: 'fire-master',
  name: 'Vuurmeester',
  description: 'Oefen 3 volle weken achter elkaar',
  emoji: '🌋',
  category: 'streak',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'blaze',
  accessory: 'doubleFlameCrown',
  check: data => data.consecutiveFullWeeks >= 3,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, 3),
    max: 3,
    label: 'weken'
  })
}, {
  id: 'hot-streak',
  name: 'Op Dreef',
  description: '10 correct op rij',
  emoji: '⚡',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'smallFlame',
  check: data => data.longestTotalStreak >= 10,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 10),
    max: 10,
    label: 'op rij'
  })
}, {
  id: 'unstoppable',
  name: 'Onstopbaar',
  description: '25 correct op rij',
  emoji: '🌟',
  category: 'streak',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'doubleFlameCrown',
  check: data => data.longestTotalStreak >= 25,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 25),
    max: 25,
    label: 'op rij'
  })
}, {
  id: 'legendary-streak',
  name: 'Legendarische Reeks',
  description: '30 correct op rij',
  emoji: '👑',
  category: 'streak',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'scepter',
  check: data => data.longestTotalStreak >= 30,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 30),
    max: 30,
    label: 'op rij'
  })
}, {
  id: 'unbreakable',
  name: 'Onbreekbaar',
  description: '10 volle weken achter elkaar',
  emoji: '💎',
  category: 'streak',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'blaze',
  accessory: 'doubleFlameCrown',
  check: data => data.consecutiveFullWeeks >= 10,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, 10),
    max: 10,
    label: 'weken'
  })
},
// Meesterschap (Mastery)
{
  id: 'first-subject',
  name: 'Eerste Vak Klaar',
  description: 'Rond je eerste vak af',
  emoji: '⭐',
  category: 'mastery',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'encouraging',
  accessory: 'starWand',
  check: data => data.masteredCount >= 1,
  getProgress: data => ({
    current: Math.min(data.masteredCount, 1),
    max: 1,
    label: 'vak'
  })
}, {
  id: 'three-subjects',
  name: 'Drie Vakken',
  description: 'Rond 3 vakken af',
  emoji: '🌟',
  category: 'mastery',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'pointing',
  djayVariantUnlocked: 'frost',
  accessory: 'tripleStars',
  check: data => data.masteredCount >= 3,
  getProgress: data => ({
    current: Math.min(data.masteredCount, 3),
    max: 3,
    label: 'vakken'
  })
}, {
  id: 'all-subjects',
  name: 'Alle Vakken',
  description: `Rond alle ${MODULES.length} vakken af`,
  emoji: '👑',
  category: 'mastery',
  rarity: 'legendary',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'royal',
  accessory: 'scepter',
  check: data => data.masteredCount >= MODULES.length,
  getProgress: data => ({
    current: Math.min(data.masteredCount, MODULES.length),
    max: MODULES.length,
    label: 'vakken'
  })
},
// Levels
{
  id: 'level-5',
  name: 'Kenner',
  description: 'Bereik level 5',
  emoji: '🌿',
  category: 'level',
  rarity: 'common',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'leafGarland',
  check: data => data.level >= 5,
  getProgress: data => ({
    current: Math.min(data.level, 5),
    max: 5,
    label: 'levels'
  })
}, {
  id: 'level-10',
  name: 'Kampioen',
  description: 'Bereik level 10',
  emoji: '🌟',
  category: 'level',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  accessory: 'medal',
  check: data => data.level >= 10,
  getProgress: data => ({
    current: Math.min(data.level, 10),
    max: 10,
    label: 'levels'
  })
}, {
  id: 'level-15',
  name: 'Ultieme Meester',
  description: 'Bereik level 15',
  emoji: '🌌',
  category: 'level',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'sleeping',
  djayVariantUnlocked: 'cosmic',
  accessory: 'cosmicOrb',
  check: data => data.level >= 15,
  getProgress: data => ({
    current: Math.min(data.level, 15),
    max: 15,
    label: 'levels'
  })
},
// Special
{
  id: 'shadow-realm',
  name: 'Schaduw Meester',
  description: 'Behaal 30.000 XP',
  emoji: '🌑',
  category: 'special',
  rarity: 'legendary',
  djayEmotionUnlocked: 'surprised',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'shadow',
  accessory: 'shadowOrb',
  check: data => data.xp >= 30000,
  getProgress: data => ({
    current: Math.min(data.xp, 30000),
    max: 30000,
    label: 'XP'
  })
}, {
  id: 'goal-getter',
  name: 'Doelpakker',
  description: 'Bereik 10x je dagelijks doel',
  emoji: '🎯',
  category: 'special',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'thinking',
  accessory: 'medal',
  check: data => data.totalDailyGoalsReached >= 10,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 10),
    max: 10,
    label: 'keer'
  })
}, {
  id: 'consistent',
  name: 'Consistent',
  description: 'Bereik 50x je dagelijks doel',
  emoji: '📅',
  category: 'special',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.totalDailyGoalsReached >= 50,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 50),
    max: 50,
    label: 'keer'
  })
}, {
  id: 'perfectionist',
  name: 'Perfectionist',
  description: '5 perfecte sessies',
  emoji: '🏆',
  category: 'special',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.totalPerfectSessions >= 5,
  getProgress: data => ({
    current: Math.min(data.totalPerfectSessions, 5),
    max: 5,
    label: 'sessies'
  })
}];
const CATEGORY_LABELS: Record<Achievement['category'], string> = {
  onboarding: 'Eerste Stappen',
  streak: 'Reeksen',
  mastery: 'Meesterschap',
  level: 'Levels',
  special: 'Speciaal'
};
const RARITY_CONFIG: Record<AchievementRarity, {
  label: string;
  cardBg: string;
  cardBorder: string;
  glowColor: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
}> = {
  common: {
    label: 'Gewoon',
    cardBg: 'bg-white',
    cardBorder: 'border-slate-200',
    glowColor: '',
    textColor: 'text-slate-600',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-500'
  },
  uncommon: {
    label: 'Ongewoon',
    cardBg: 'bg-gradient-to-br from-emerald-50 to-teal-50',
    cardBorder: 'border-emerald-200',
    glowColor: 'shadow-emerald-200/50',
    textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-600'
  },
  rare: {
    label: 'Zeldzaam',
    cardBg: 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50',
    cardBorder: 'border-amber-300',
    glowColor: 'shadow-amber-300/60',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700'
  },
  legendary: {
    label: 'Legendarisch',
    cardBg: 'bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50',
    cardBorder: 'border-purple-300',
    glowColor: 'shadow-purple-400/50',
    textColor: 'text-purple-700',
    badgeBg: 'bg-gradient-to-r from-purple-500 to-fuchsia-500',
    badgeText: 'text-white'
  }
};
// Sparkle particle component for rare/legendary
function SparkleParticles({
  count = 6,
  color = 'amber'
}: {
  count?: number;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    amber: 'bg-amber-400',
    purple: 'bg-purple-400',
    fuchsia: 'bg-fuchsia-400'
  };
  // Stable random positions — computed once per mount, not on every re-render
  const positions = useMemo(() => Array.from({
    length: count
  }).map(() => ({
    left: `${15 + Math.random() * 70}%`,
    top: `${15 + Math.random() * 70}%`
  })), [count]);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1460">
      {positions.map((pos, i) => <motion.div key={i} className={`absolute w-1.5 h-1.5 rounded-full ${colorMap[color] || colorMap.amber}`} style={pos} animate={{
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      y: [0, -10, -20]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      delay: i * 0.3,
      ease: 'easeOut'
    }} data-id="element-1461" />)}
    </div>;
}
// Golden shimmer effect for rare achievements
function GoldenShimmer() {
  return <motion.div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} data-id="element-1462">
      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/40 to-transparent" animate={{
      x: ['-100%', '200%']
    }} transition={{
      duration: 3,
      repeat: Infinity,
      repeatDelay: 2,
      ease: 'easeInOut'
    }} data-id="element-1463" />
    </motion.div>;
}
// Legendary pulsing glow
function LegendaryGlow() {
  return <motion.div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-30 blur-md pointer-events-none" animate={{
    opacity: [0.2, 0.4, 0.2],
    scale: [1, 1.02, 1]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-1464" />;
}
// Djay wrapper with rarity-specific visual treatment
function RarityDjayWrapper({
  rarity,
  emotion,
  variant = 'default',
  accessory = 'none',
  isUnlocked,
  animate
}: {
  rarity: AchievementRarity;
  emotion: DjayEmotion;
  variant?: DjayVariant;
  accessory?: DjayAccessory;
  isUnlocked: boolean;
  animate: boolean;
}) {
  // Always use 'sm' for compact cards
  const baseSize = 'sm';
  if (!isUnlocked) {
    return <div className="relative" data-id="element-1465">
        <div className="grayscale opacity-50" data-id="element-1466">
          <DjayAvatar size={baseSize} emotion={emotion} variant="default" accessory="none" animate={false} data-id="element-1467" />
        </div>
      </div>;
  }
  switch (rarity) {
    case 'legendary':
      return <div className="relative" data-id="element-1468">
          {/* Pulsing glow ring */}
          <motion.div className="absolute -inset-2 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-40 blur-sm" animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1469" />
          {/* Crown overlay */}
          <motion.div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 text-2xl" animate={{
          y: [0, -3, 0],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1470">
            👑
          </motion.div>
          <div className="relative z-10" data-id="element-1471">
            <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1472" />
          </div>
        </div>;
    case 'rare':
      return <div className="relative" data-id="element-1473">
          {/* Golden glow ring */}
          <motion.div className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 opacity-50 blur-sm" animate={{
          opacity: [0.4, 0.6, 0.4]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-1474" />
          <div className="relative z-10" data-id="element-1475">
            <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1476" />
          </div>
          {/* Star sparkle */}
          <motion.div className="absolute -top-1 -right-1 z-20" animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1477">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-1478" />
          </motion.div>
        </div>;
    case 'uncommon':
      return <div className="relative" data-id="element-1479">
          {/* Subtle glow ring */}
          <div className="absolute -inset-1 rounded-full bg-emerald-300/30 blur-sm" data-id="element-1480" />
          <div className="relative z-10" data-id="element-1481">
            <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1482" />
          </div>
        </div>;
    default:
      return <DjayAvatar size={baseSize} emotion={emotion} variant={variant} accessory={accessory} animate={animate} data-id="element-1483" />;
  }
}
export const AchievementsSection = memo(function AchievementsSection({
  state,
  totalQuestionsAnswered,
  masteredCount,
  currentStreak
}: AchievementsSectionProps) {
  const [showAll, setShowAll] = useState(false);
  // Extract streak data from state
  const currentWeekDays = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  const longestTotalStreak = state.streak?.longestTotalStreak || 0;
  // Lifetime counters from state (HC-7/HC-8 fix: now properly tracked)
  // Fallback: derive totalDailyGoalsReached from weeklyHistory for pre-migration data
  const totalDailyGoalsReached = state.dailyStreak?.totalDailyGoalsReached ?? (state.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state.dailyStreak?.totalPerfectSessions ?? 0;
  const checkData: AchievementCheckData = useMemo(() => ({
    totalQuestionsAnswered,
    masteredCount,
    level: state.stats.level,
    xp: state.stats.xp,
    currentWeekDays,
    consecutiveFullWeeks,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions
  }), [totalQuestionsAnswered, masteredCount, state.stats.level, state.stats.xp, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions]);
  const achievementsByCategory = useMemo(() => {
    return ACHIEVEMENTS.map(achievement => ({
      ...achievement,
      isUnlocked: achievement.check(checkData),
      progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
    }));
  }, [checkData]);
  const unlockedCount = achievementsByCategory.filter(a => a.isUnlocked).length;
  const totalCount = achievementsByCategory.length;
  // Compute focus sections
  const {
    nextUp,
    recentlyUnlocked
  } = useMemo(() => {
    // Next up: locked achievements sorted by progress %
    const locked = achievementsByCategory.filter(a => !a.isUnlocked && a.progress);
    const sortedLocked = locked.sort((a, b) => {
      const pctA = a.progress ? a.progress.current / a.progress.max : 0;
      const pctB = b.progress ? b.progress.current / b.progress.max : 0;
      return pctB - pctA;
    });
    const nextUpList = sortedLocked.slice(0, 3);
    // Recently unlocked: unlocked achievements, prioritize rare/legendary
    const unlocked = achievementsByCategory.filter(a => a.isUnlocked);
    const rarityWeight = {
      legendary: 4,
      rare: 3,
      uncommon: 2,
      common: 1
    };
    const sortedUnlocked = unlocked.sort((a, b) => rarityWeight[b.rarity] - rarityWeight[a.rarity]);
    const recentlyUnlockedList = sortedUnlocked.slice(0, 3);
    return {
      nextUp: nextUpList,
      recentlyUnlocked: recentlyUnlockedList
    };
  }, [achievementsByCategory]);
  // Group achievements by category
  const groupedAchievements = useMemo(() => {
    const groups: Record<Achievement['category'], typeof achievementsByCategory> = {
      onboarding: [],
      streak: [],
      mastery: [],
      level: [],
      special: []
    };
    achievementsByCategory.forEach(a => {
      groups[a.category].push(a);
    });
    return groups;
  }, [achievementsByCategory]);
  const progressPercentage = Math.round(unlockedCount / totalCount * 100);
  // Count by rarity
  const rarityCounts = useMemo(() => {
    const counts: Record<AchievementRarity, {
      total: number;
      unlocked: number;
    }> = {
      common: {
        total: 0,
        unlocked: 0
      },
      uncommon: {
        total: 0,
        unlocked: 0
      },
      rare: {
        total: 0,
        unlocked: 0
      },
      legendary: {
        total: 0,
        unlocked: 0
      }
    };
    achievementsByCategory.forEach(a => {
      counts[a.rarity].total++;
      if (a.isUnlocked) counts[a.rarity].unlocked++;
    });
    return counts;
  }, [achievementsByCategory]);
  return <div className="space-y-6" data-id="element-1484">
      {/* Header with rarity breakdown and progress ring */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-4 sm:p-5 rounded-2xl border-2 border-slate-100 shadow-sm" data-id="element-1485">
        <div className="flex items-center gap-4" data-id="element-1486">
          <div className="relative w-14 h-14 flex-shrink-0" data-id="element-1487">
            <svg className="w-14 h-14 transform -rotate-90" data-id="element-1488">
              <circle cx="28" cy="28" r="24" fill="none" stroke="#F1F5F9" strokeWidth="6" data-id="element-1489" />
              <circle cx="28" cy="28" r="24" fill="none" stroke="#F59E0B" strokeWidth="6" strokeDasharray="150.8" strokeDashoffset={150.8 - progressPercentage / 100 * 150.8} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1490" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-1491">
              <Sparkles className="w-5 h-5 text-amber-500" data-id="element-1492" />
            </div>
          </div>
          <div data-id="element-1493">
            <h2 className="text-xl font-black text-slate-800" data-id="element-1494">Achievements</h2>
            <p className="text-sm text-slate-500 font-medium" data-id="element-1495">
              {unlockedCount} van {totalCount} behaald
            </p>
          </div>
        </div>

        {/* Rarity breakdown pills */}
        <div className="flex flex-wrap gap-2" data-id="element-1496">
          {(['legendary', 'rare', 'uncommon', 'common'] as AchievementRarity[]).map(rarity => {
          const config = RARITY_CONFIG[rarity];
          const counts = rarityCounts[rarity];
          if (counts.total === 0) return null;
          return <div key={rarity} className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${config.badgeBg} ${config.badgeText}`} data-id="element-1497">
                {counts.unlocked}/{counts.total} {config.label}
              </div>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1498">
        {/* Recent behaald */}
        {recentlyUnlocked.length > 0 && <div className="space-y-3" data-id="element-1499">
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-400 flex items-center gap-2" data-id="element-1500">
              <Star className="w-4 h-4" data-id="element-1501" /> Recent behaald
            </h3>
            <div className="space-y-3" data-id="element-1502">
              {recentlyUnlocked.map((achievement, index) => <CompactAchievementCard key={achievement.id} achievement={achievement} isUnlocked={true} index={index} showEffects={true} data-id="element-1503" />)}
            </div>
          </div>}

        {/* Volgende te behalen */}
        {nextUp.length > 0 && <div className="space-y-3" data-id="element-1504">
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-400 flex items-center gap-2" data-id="element-1505">
              <Lock className="w-4 h-4" data-id="element-1506" /> Volgende doelen
            </h3>
            <div className="space-y-3" data-id="element-1507">
              {nextUp.map((achievement, index) => <CompactAchievementCard key={achievement.id} achievement={achievement} isUnlocked={false} index={index} showEffects={false} data-id="element-1508" />)}
            </div>
          </div>}
      </div>

      {/* Expand Button */}
      <div className="flex justify-center pt-2" data-id="element-1509">
        <Button variant="secondary" onClick={() => setShowAll(!showAll)} className="bg-white border-2 border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 rounded-full px-6" data-id="element-1510">
          {showAll ? <>
              Verberg alle achievements <ChevronUp className="w-4 h-4 ml-2" data-id="element-1511" />
            </> : <>
              Bekijk alle achievements <ChevronDown className="w-4 h-4 ml-2" data-id="element-1512" />
            </>}
        </Button>
      </div>

      {/* Full Grid (Expandable) */}
      <AnimatePresence data-id="element-1513">
        {showAll && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-1514">
            <div className="space-y-8 pt-4 border-t-2 border-slate-100" data-id="element-1515">
              {(Object.keys(groupedAchievements) as Achievement['category'][]).map(category => {
            const achievements = groupedAchievements[category];
            if (achievements.length === 0) return null;
            return <div key={category} data-id="element-1516">
                    <h3 className="text-sm font-black uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2" data-id="element-1517">
                      <span data-id="element-1518">{CATEGORY_LABELS[category]}</span>
                      <span className="text-slate-300" data-id="element-1519">
                        ({achievements.filter(a => a.isUnlocked).length}/
                        {achievements.length})
                      </span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1520">
                      {achievements.map((achievement, index) => <AchievementCard key={achievement.id} achievement={achievement} isUnlocked={achievement.isUnlocked} index={index} data-id="element-1521" />)}
                    </div>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
});
interface AchievementCardProps {
  achievement: Achievement & {
    isUnlocked: boolean;
    progress?: {
      current: number;
      max: number;
      label: string;
    };
  };
  isUnlocked: boolean;
  index: number;
}
function CompactAchievementCard({
  achievement,
  isUnlocked,
  index,
  showEffects
}: AchievementCardProps & {
  showEffects: boolean;
}) {
  const config = RARITY_CONFIG[achievement.rarity];
  const djayEmotion = isUnlocked ? achievement.djayEmotionUnlocked : achievement.djayEmotionLocked;
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay: index * 0.1
  }} className={`relative flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${isUnlocked ? `${config.cardBg} ${config.cardBorder} ${showEffects && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') ? `shadow-md ${config.glowColor}` : 'shadow-sm'}` : 'bg-slate-50 border-slate-200 opacity-80'}`} data-id="element-1522">
      {isUnlocked && showEffects && achievement.rarity === 'legendary' && <LegendaryGlow data-id="element-1523" />}
      {isUnlocked && showEffects && achievement.rarity === 'rare' && <GoldenShimmer data-id="element-1524" />}

      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-white/50 rounded-xl" data-id="element-1525">
        <span className="text-2xl" data-id="element-1526">{achievement.emoji}</span>
      </div>

      <div className="flex-1 min-w-0" data-id="element-1527">
        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-1528">
          <h4 className={`font-bold text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1529">
            {achievement.name}
          </h4>
          {isUnlocked && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') && <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold ${config.badgeBg} ${config.badgeText}`} data-id="element-1530">
                {config.label}
              </span>}
        </div>

        {!isUnlocked && achievement.progress ? <div className="w-full" data-id="element-1531">
            <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-1" data-id="element-1532">
              <span data-id="element-1533">{achievement.description}</span>
              <span data-id="element-1534">
                {achievement.progress.current}/{achievement.progress.max}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1535">
              <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{
            width: `${achievement.progress.current / achievement.progress.max * 100}%`
          }} data-id="element-1536" />
            </div>
          </div> : <p className={`text-xs truncate ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1537">
            {achievement.description}
          </p>}
      </div>
    </motion.div>;
}
function AchievementCard({
  achievement,
  isUnlocked,
  index
}: AchievementCardProps) {
  const config = RARITY_CONFIG[achievement.rarity];
  const djayEmotion = isUnlocked ? achievement.djayEmotionUnlocked : achievement.djayEmotionLocked;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.02
  }} className="relative h-full" data-id="element-1538">
      <Card className={`
          relative overflow-hidden p-3 transition-all duration-300 border-2 h-full flex flex-col
          ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-70'}
          ${isUnlocked ? 'hover:shadow-md hover:-translate-y-0.5' : ''}
        `} data-id="element-1539">
        {!isUnlocked && <div className="absolute top-2 right-2 z-10" data-id="element-1540">
            <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shadow-inner" data-id="element-1541">
              <Lock className="w-3 h-3 text-slate-400" data-id="element-1542" />
            </div>
          </div>}

        {isUnlocked && (achievement.rarity === 'rare' || achievement.rarity === 'legendary') && <div className="absolute top-2 right-2 z-10" data-id="element-1543">
              <div className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold shadow-sm ${config.badgeBg} ${config.badgeText}`} data-id="element-1544">
                {config.label}
              </div>
            </div>}

        <div className="flex items-start gap-3 relative z-10 flex-1" data-id="element-1545">
          <div className="flex-shrink-0" data-id="element-1546">
            <RarityDjayWrapper rarity={achievement.rarity} emotion={djayEmotion} variant={achievement.djayVariantUnlocked} accessory={achievement.accessory} isUnlocked={isUnlocked} animate={isUnlocked} data-id="element-1547" />
          </div>

          <div className="flex-1 min-w-0 pt-0.5 flex flex-col h-full" data-id="element-1548">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-1549">
              <span className="text-base" data-id="element-1550">{achievement.emoji}</span>
              <h4 className={`font-black text-xs truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1551">
                {achievement.name}
              </h4>
            </div>
            <p className={`text-[10px] leading-snug mb-2 flex-1 ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1552">
              {achievement.description}
            </p>

            {!isUnlocked && achievement.progress && <div className="mt-auto pt-1" data-id="element-1553">
                <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-0.5" data-id="element-1554">
                  <span data-id="element-1555">Voortgang</span>
                  <span data-id="element-1556">
                    {achievement.progress.current} / {achievement.progress.max}
                  </span>
                </div>
                <div className="h-1 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-1557">
                  <div className="h-full bg-slate-400 rounded-full transition-all duration-500" style={{
                width: `${achievement.progress.current / achievement.progress.max * 100}%`
              }} data-id="element-1558" />
                </div>
              </div>}
          </div>
        </div>
      </Card>
    </motion.div>;
}